<template>
  <AulaTemplate
    :titulo="$store.state.topicos[0].titulo"
    :numero="$store.state.topicos[0].numero"
    titleSize="5"
  >
    <TextBlock animation="zoom-in">
      <div class="youtube-container" style="max-width: 600px">
        <youtube
          video-id="gB0TRjTQX6E"
          class="mt-8 elevation-10"
          style="border: solid 3px white"
          fit-parent
        />
      </div>
    </TextBlock>

    <TextBlock subTopico="1.1  Trafegando com o seu veículo">
      <Paragraph>
        Vamos iniciar nossa conversa explorando um aspecto fundamental para quem
        dirige: a <strong>documentação necessária</strong>. Ter a documentação
        em ordem é essencial não apenas para estar em conformidade com a
        legislação, mas também para garantir uma condução tranquila e sem
        contratempos. Saber quais documentos são obrigatórios e como mantê-los
        atualizados pode evitar problemas e assegurar que você esteja sempre
        pronto para qualquer situação que surja nas estradas.
      </Paragraph>
    </TextBlock>

    <v-row justify="center" class="mt-8">
      <v-col cols="12" lg="7">
        <Imagem src="modulo_01/frase_balao.svg" />
      </v-col>
    </v-row>

    <TextBlock>
      <Paragraph>
        Então, preste atenção: para dirigir com segurança e legalidade, alguns
        documentos são indispensáveis. Primeiro, a famosa CNH, ou Carteira
        Nacional de Habilitação, é essencial, pois é o comprovante de que você
        está apto a dirigir. Além disso, há o CRLV, que é o Certificado de
        Registro e Licenciamento de Veículo, e confirma que o carro está
        regularizado.
      </Paragraph>
    </TextBlock>

    <v-row justify="center" class="mt-5">
      <v-col cols="12" lg="auto">
        <SmallText class="text-center"
          >Imagem 1: CNH e CRLV em ordem: segurança e responsabilidade no
          trânsito.</SmallText
        >
        <FlipImage
          data-aos="zoom-in"
          data-aos-easing="ease"
          data-aos-duration="500"
          data-aos-once="true"
          bgImgFront="modulo_01/cnh-frente.jpg"
          bgImgBack="modulo_01/cnh-verso.jpg"
        >
        </FlipImage>
      </v-col>
    </v-row>

    <v-row justify="center" class="mt-5">
      <v-col cols="12" lg="9">
        <Imagem src="modulo_01/CRLV 2024.png" fonte="imagens geradas por IA" />
      </v-col>
    </v-row>

    <TextBlock>
      <Audio_1 />
    </TextBlock>

    <TextBlock subTopico="1.2  Regras Básicas de Circulação segundo o CTB">
      <Paragraph>
        Para dirigir em via pública, é essencial seguir as regras estabelecidas
        pelo Código de Trânsito Brasileiro (CTB). Essas normas garantem a
        segurança e a ordem no trânsito, e é fundamental conhecê-las para evitar
        problemas e contribuir para um ambiente mais seguro para todos.
      </Paragraph>
    </TextBlock>

    <BoxIcon tipo="voce_sabia">
      O Código de Trânsito Brasileiro de 1997 é considerado um divisor de águas
      na legislação de trânsito do país, pois trouxe uma abordagem mais
      abrangente e moderna. Seu principal objetivo foi alinhar as normas de
      trânsito com as necessidades de um país em desenvolvimento e uma frota de
      veículos em constante expansão, proporcionando uma regulamentação mais
      adequada e eficaz.
    </BoxIcon>

    <TextBlock>
      <Paragraph>
        O art. 29 estabelece as regras básicas para a circulação de veículos nas
        vias públicas, como a prioridade de passagem, a forma correta de
        utilizar as faixas de trânsito, a obrigação de manter uma distância
        segura entre os veículos, entre outras.Vejamos as principais regras
        estabelecidas pelo art. 29 do CTB (BRASIL, 2020, p.26-28):
      </Paragraph>
    </TextBlock>

    <TextBlock v-if="$vuetify.breakpoint.lgAndUp">
      <v-row>
        <v-col cols="12">
          <Infografico_1></Infografico_1>
        </v-col>
      </v-row>
    </TextBlock>

    <TextBlock v-else>
      <Table>
        <thead>
          <tr>
            <th
              class="primary white--text text-body-2 text-uppercase font-weight-bold"
            >
              Principais regras estabelecidas pelo art. 29 do CTB (BRASIL, 2020,
              p.26-28)
            </th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td>
              I - a circulação far-se-á pelo lado direito da via, admitindo-se
              as exceções devidamente sinalizadas;
            </td>
          </tr>
          <tr>
            <td>
              III - Quando uma pista de rolamento comportar várias faixas de
              circulação no mesmo sentido, são as da direita destinadas ao
              deslocamento dos veículos mais lentos e de maior porte, quando não
              houver faixa especial a eles destinada, e as da esquerda,
              destinadas à ultrapassagem e ao deslocamento dos veículos de maior
              velocidade;
            </td>
          </tr>
          <tr>
            <td>
              IV - O trânsito de veículos sobre passeios, calçadas e nos
              acostamentos, só poderá ocorrer para que se adentre ou se saia dos
              imóveis ou áreas especiais de estacionamento;
            </td>
          </tr>
          <tr>
            <td>
              V - Os veículos precedidos de batedores terão prioridade de
              passagem, respeitadas as demais normas de circulação;
            </td>
          </tr>
          <tr>
            <td>
              VI - Os veículos destinados a socorro de incêndio e salvamento, os
              de polícia, os de fiscalização e operação de trânsito e as
              ambulâncias, além de prioridade no trânsito, gozam de livre
              circulação, estacionamento e parada, quando em serviço de
              urgência, de policiamento ostensivo ou de preservação da ordem
              pública, observadas as seguintes disposições (Redação dada pela
              Lei 14.071, de 2020):
            </td>
          </tr>
        </tbody>
      </Table>
    </TextBlock>

    <BoxIcon tipo="atencao">
      <p class="mb-3">
        Seguem dois artigos muito importantes para você lembrar e colocar em
        prática:
      </p>
      <ul>
        <li>
          <strong>Art. 27:</strong> Antes de colocar o veículo em circulação nas
          vias públicas, o condutor deverá adotar procedimentos de verificação
          das condições gerais do seu veículo.
        </li>
        <li>
          <strong>Art. 28:</strong> O condutor deverá, a todo momento, ter
          domínio de seu veículo, dirigindo-o com atenção e cuidados
          indispensáveis à segurança do trânsito. (BRASIL, 2 ed - 2009, p.26)
        </li>
      </ul>
    </BoxIcon>

    <TextBlock>
      <Audio_2 />
    </TextBlock>

    <TextBlock subTopico="1.3  Restrições de uso de Vias">
      <Paragraph>
        O regulamento de trânsito não é só sobre sinais e multas - ele também
        prevê restrições de uso das vias, que incluem limites de velocidade,
        regras de estacionamento e diretrizes para a utilização de veículos em
        determinadas áreas e horários. Vamos explorar essas restrições e
        entender como elas contribuem para um trânsito mais eficiente e
        tranquilo?
      </Paragraph>
    </TextBlock>

    <BoxIcon tipo="voce_sabia">
      Em áreas urbanas, o limite máximo de velocidade para uma via arterial é de
      <strong>60 km/h</strong>, conforme estabelecido pelo
      <strong>Código de Trânsito Brasileiro (CTB)</strong>. No entanto, esse
      limite pode ser reduzido para <strong>40 km/h</strong> em áreas escolares
      e residenciais, visando aumentar a segurança dos pedestres. Em vias de
      trânsito rápido, o limite máximo de velocidade é de
      <strong>80 km/h</strong>. Além disso, é essencial respeitar as faixas
      exclusivas para ônibus e ciclovias, pois não cumprir essas regras pode
      resultar em multas e outras penalidades previstas na legislação.
    </BoxIcon>

    <v-row justify="center" class="mt-5">
      <v-col cols="12" lg="6">
        <Imagem
          src="modulo_01/img2.png"
          legenda="Imagem 2: Vias urbanas e rurais possuem marcações de limites de velocidade e sinais de restrição."
          fonte="imagens geradas por IA"
        />
      </v-col>
    </v-row>

    <TextBlock>
      <Paragraph>
        O regulamento de trânsito estabelece a velocidade máxima permitida em
        cada via por meio de placas de sinalização, e isso você já sabe. Mas e
        quando não há sinalização? Vejamos a seguir quais limites gerais
        estabelecidos pelo Código de Trânsito Brasileiro:
      </Paragraph>
    </TextBlock>

    <v-row justify="center" class="mt-5">
      <v-col cols="12" lg="6">
        <Imagem
          src="modulo_01/tipos-rodovias.png"
          legenda="Imagem 3 - Limites de Velocidade"
          fonte="(BRASIL, 1997; BRASIL, 2022) "
        />
      </v-col>
    </v-row>

    <!-- <Table>
      <thead>
        <tr>
          <th
            class="primary white--text text-body-2 text-uppercase font-weight-bold"
          >
            Tipo de Via / Rodovia / Estrada
          </th>
          <th
            class="primary white--text text-body-2 text-uppercase font-weight-bold"
          >
            Limite de Velocidade
          </th>
        </tr>
      </thead>

      <tbody>
        <tr class="primary lighten-3 text-center font-weight-bold">
          <td colspan="2">Vias Urbanas</td>
        </tr>

        <tr>
          <td>Vias de trânsito rápido</td>
          <td>80 km/h</td>
        </tr>

        <tr>
          <td>Vias arteriais</td>
          <td>60 km/h</td>
        </tr>

        <tr>
          <td>Vias coletoras</td>
          <td>40 km/h</td>
        </tr>

        <tr>
          <td>Vias locais</td>
          <td>30 km/h</td>
        </tr>

        <tr class="primary lighten-3 text-center font-weight-bold">
          <td colspan="2">Rodovias de Pista Dupla</td>
        </tr>

        <tr>
          <td>Automóveis, camionetas, caminhonetes e motocicletas</td>
          <td>110 km/h</td>
        </tr>

        <tr>
          <td>Demais veículos</td>
          <td>90 km/h</td>
        </tr>

        <tr class="primary lighten-3 text-center font-weight-bold">
          <td colspan="2">Rodovias de Pista Simples</td>
        </tr>

        <tr>
          <td>Automóveis, camionetas, caminhonetes e motocicletas</td>
          <td>100 km/h</td>
        </tr>

        <tr>
          <td>Demais veículos</td>
          <td>90 km/h</td>
        </tr>

        <tr class="primary lighten-3 text-center font-weight-bold">
          <td colspan="2">Estradas</td>
        </tr>

        <tr>
          <td>Vias rurais não pavimentadas</td>
          <td>60 km/h</td>
        </tr>
      </tbody>
    </Table> -->

    <BoxIcon tipo="atencao">
      Fique de olho no velocímetro! A velocidade excessiva é um dos principais
      fatores de risco para acidentes de trânsito e está diretamente ligada à
      gravidade das colisões. Muitos motoristas acreditam que dirigir em alta
      velocidade pode ajudá-los a escapar de situações difíceis no trânsito, mas
      isso não é verdade. Na realidade, reduzir a velocidade deve ser a primeira
      medida para evitar acidentes.
    </BoxIcon>

    <TextBlock>
      <Paragraph>
        Para entender melhor a organização do trânsito e a função de cada tipo
        de via, é importante conhecer as características e classificações
        estabelecidas para as vias e rodovias (BRASIL, 1997; BRASIL, 2022):
      </Paragraph>
    </TextBlock>

    <TextBlock>
      <Accordion>
        <v-expansion-panel>
          <v-expansion-panel-header
            >Via de trânsito rápido</v-expansion-panel-header
          >
          <v-expansion-panel-content>
            Caracterizada por acessos especiais com trânsito livre, sem
            interseções em nível, sem acessibilidade direta aos lotes lindeiros
            e sem travessia de pedestres em nível.
          </v-expansion-panel-content>
        </v-expansion-panel>

        <v-expansion-panel>
          <v-expansion-panel-header>Via arterial</v-expansion-panel-header>
          <v-expansion-panel-content>
            Caracterizada por interseções em nível, geralmente controlada por
            semáforo, com acessibilidade aos lotes lindeiros e às vias
            secundárias e locais, possibilitando o trânsito entre as regiões da
            cidade.
          </v-expansion-panel-content>
        </v-expansion-panel>

        <v-expansion-panel>
          <v-expansion-panel-header>Via coletora</v-expansion-panel-header>
          <v-expansion-panel-content>
            Destinada a coletar e distribuir o trânsito que tenha necessidade de
            entrar ou sair das vias de trânsito rápido ou arteriais,
            possibilitando o trânsito dentro das regiões da cidade.
          </v-expansion-panel-content>
        </v-expansion-panel>

        <v-expansion-panel>
          <v-expansion-panel-header>Via local</v-expansion-panel-header>
          <v-expansion-panel-content>
            Caracterizada por interseções em nível não semaforizadas, destinada
            apenas ao acesso local ou a áreas restritas.
          </v-expansion-panel-content>
        </v-expansion-panel>

        <v-expansion-panel>
          <v-expansion-panel-header>Via rural</v-expansion-panel-header>
          <v-expansion-panel-content>
            Distingue-se tão somente pela existência ou não de pavimentação: as
            vias rurais pavimentadas são denominadas rodovias e as não
            pavimentadas são as estradas (entende-se pavimento).
          </v-expansion-panel-content>
        </v-expansion-panel>
      </Accordion>
    </TextBlock>

    <BoxIcon tipo="curiosidade">
      As rodovias podem ser construídas com <strong>pista simples</strong>, onde
      os veículos transitam em direções opostas na mesma faixa de rodagem. Por
      outro lado, as <strong>pistas duplas</strong> possuem duas faixas de
      rodagem separadas, geralmente por um canteiro central ou uma barreira
      física. Cada faixa é destinada a veículos que trafegam no mesmo sentido, o
      que aumenta a segurança e a fluidez do trânsito.
    </BoxIcon>

    <BoxIcon tipo="voce_sabia">
      Você sabia que há uma velocidade mínima obrigatória para conduzir o seu
      veículo? Segundo o
      <strong>Artigo 62 do Código de Trânsito Brasileiro (CTB)</strong>, a
      velocidade mínima em uma via deve ser, no mínimo, metade da velocidade
      máxima permitida. Por exemplo, se a velocidade máxima é
      <strong>80 km/h</strong>, a mínima deve ser <strong>40 km/h</strong>.
    </BoxIcon>

    <TextBlock subTopico="1.4 Fundamentos de Parar e Estacionar">
      <v-row justify="center" class="mt-5">
        <v-col cols="12" lg="7">
          <Imagem src="modulo_01/frase_balao_2.svg" />
        </v-col>
      </v-row>

      <TextBlock>
        <Paragraph>
          No Código de Trânsito Brasileiro, há uma diferença importante entre
          <strong>parar</strong> e <strong>estacionar</strong>.
          <strong>Parar</strong> significa que o veículo ficará imobilizado por
          um breve período, como quando você permanece ao volante, aguardando
          alguém entrar ou sair do carro ou realizando uma descarga rápida. Já
          <strong>estacionar</strong> é quando o veículo é deixado parado por um
          tempo mais prolongado, como ao colocá-lo em uma vaga para resolver
          seus compromissos, seja em estacionamentos ou vagas na rua; o
          importante é que o veículo ficará ali por um período maior.
        </Paragraph>
      </TextBlock>
    </TextBlock>

    <TextBlock>
      <Audio_3 />
    </TextBlock>

    <TextBlock>
      <Paragraph>
        Para operações de carga e descarga, o veículo deve ser posicionado
        paralelo à pista, junto ao meio-fio, preferencialmente em
        estacionamentos privados ou em locais e horários regulamentados,
        conforme indicado pela sinalização ou placas de regulamentação na via
        pública.
      </Paragraph>
    </TextBlock>

    <TextBlock subTopico="1.5 Cruzamento de Vias">
      <v-row justify="center" class="mt-8">
        <v-col cols="12" lg="7">
          <Imagem src="modulo_01/frase_balao_3.svg" />
        </v-col>
      </v-row>

      <Paragraph class="my-8">
        Você lembra quais são as regras de preferência para situações de
        cruzamento de vias? Saber quem tem a prioridade de passagem é
        fundamental para evitar acidentes e garantir um trânsito mais seguro
        para todos. Vamos relembrar essas regras essenciais para uma direção
        consciente (CTB, Artigo 29, Inciso III):
      </Paragraph>

      <v-row>
        <v-col cols="12" md="4">
          <FlipCardImg bgImg="modulo_01/carro1.jpg">
            <template #front>Caso A</template>

            <template #back>
              <span style="font-size: 1.3rem">
                No caso de apenas um fluxo ser proveniente de rodovia, aquele
                que estiver circulando por ela;
              </span>
            </template>
          </FlipCardImg>
        </v-col>

        <v-col cols="12" md="4">
          <FlipCardImg bgImg="modulo_01/carro2.jpg">
            <template #front>Caso B</template>

            <template #back>
              <span style="font-size: 1.3rem">
                No caso de rotatória, aquele que estiver circulando por ela;
              </span>
            </template>
          </FlipCardImg>
        </v-col>

        <v-col cols="12" md="4">
          <FlipCardImg bgImg="modulo_01/carro3.jpg">
            <template #front>Caso C</template>

            <template #back>
              <span style="font-size: 1.3rem">
                Nos demais casos, o que vier pela direita do condutor.
              </span>
            </template>
          </FlipCardImg>
        </v-col>
      </v-row>
    </TextBlock>

    <BoxIcon tipo="curiosidade">
      Quer entender melhor sobre as regras de preferência em cruzamentos sem
      sinalização? Assista ao vídeo que explica de forma prática e visual como
      agir nessas situações:
      <a
        :style="{ color: $vuetify.theme.dark ? '#111' : '#fff' }"
        href="https://youtu.be/XOwOqMJ_aWA"
        target="_blank"
        >https://youtu.be/XOwOqMJ_aWA</a
      >
    </BoxIcon>

    <div ref="PitStopDoSaber_1">
      <PitStopDoSaber route="topico-1-atividade-1" />
    </div>

    <BoxIcon tipo="recapitulando"
      >Chegamos ao final do tópico 1. Continue atento às normas e pratique uma
      direção responsável para contribuir com um ambiente mais seguro para
      todos. Pronto para aplicar esses conceitos na prática? Vamos em frente!
    </BoxIcon>
  </AulaTemplate>
</template>

<script>
import AulaTemplate from "@/components/AulaTemplate.vue";
import TextBlock from "@/components/TextBlock.vue";
import Paragraph from "@/components/Paragraph.vue";
import BoxIcon from "@/components/BoxIcon.vue";
import Imagem from "@/components/Imagem.vue";
import Table from "@/components/Table.vue";
import Accordion from "@/components/Accordion.vue";
import FlipCardImg from "@/components/FlipCardImg.vue";
import FlipImage from "@/components/FlipImage.vue";
import Audio_1 from "./audios/Audio_1.vue";
import Audio_2 from "./audios/Audio_2.vue";
import Audio_3 from "./audios/Audio_3.vue";
import PitStopDoSaber from "@/components/PitStopDoSaber.vue";
import SmallText from "@/components/SmallText.vue";
import Infografico_1 from "./atividades/Infografico_1.vue";

export default {
  components: {
    AulaTemplate,
    SmallText,
    TextBlock,
    Paragraph,
    BoxIcon,
    Imagem,
    Table,
    Accordion,
    FlipCardImg,
    FlipImage,
    Audio_1,
    Audio_2,
    Audio_3,
    PitStopDoSaber,
    Infografico_1,
  },
  data() {
    return {
      aluno: "",
      glossario1: false,
      dialog: false,
    };
  },

  methods: {
    dialogInput(value) {
      if (!value) {
        // Acessa o player do vídeo através da referência ref
        const player = this.$refs.videoPlayer.player;

        if (player && typeof player.pauseVideo === "function") {
          player.pauseVideo();
        }
      }
    },

    goToPitStop_1() {
      let element = this.$refs["PitStopDoSaber_1"];
      let top = element.offsetTop - 60;
      window.scrollTo({
        top,
        behavior: "smooth",
      });
    },
  },

  mounted() {
    this.$store.commit("ADICIONAR_SCORE", "topico_01");
    if (this.$route.query.goTo === "goToPitStop_1") {
      this.goToPitStop_1();
    }
  },
};
</script>

<style scoped></style>
